import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CheckboxListCard,
  CheckboxListItem,
  Highlight,
  IconBannersWarning,
  HStack,
  Text,
  VStack,
  ScrollView,
} from '@adc/parallax-component-library';
import { Dialog } from 'tamagui';
import {
  Account,
  AccountWithRoleAssignments,
  ApiErrorResponse,
  Role,
} from '../../models';
import { useAppSelector } from '../../reducers/hooks';
import i18n from '../../utilities/i18n';
import SearchableDropdown, {
  SearchableDropdownItem,
} from '../searchableDropdown/SearchableDropdown';
import RoleAssignmentsConfirmationModal from './RoleAssignmentsConfirmationModal';
import './Modal.scss';

interface RoleAssignmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAccount?: AccountWithRoleAssignments | null;
  accountsWithRoleAssignments?: AccountWithRoleAssignments[];
  availableRoles: Role[];
  isSaving?: boolean;
  onSave: (
    account: AccountWithRoleAssignments,
    addRoleKeys: string[],
    removeRoleKeys: string[],
  ) => Promise<void>;
  allAccounts?: Account[];
}

const getAccountIdentity = (account: Account) => {
  return `${account.firstName} ${account.lastName} (${account.email})`;
};

const getRoleKeys = (account: AccountWithRoleAssignments | null) => {
  if (!account) {
    return [];
  }

  return account.roleAssignments.map(
    (roleAssignment) => roleAssignment.role.key,
  );
};

const hasPermittedRoleAssignmentEmailDomain = (
  email: string,
  allowedDomains: string[],
) => {
  const emailParts = email.trim().toLowerCase().split('@');

  if (emailParts.length !== 2 || !emailParts[0] || !emailParts[1]) {
    return false;
  }

  const emailDomain = emailParts[1];

  return allowedDomains.some(
    (domain) => domain.trim().toLowerCase().replace(/^@/, '') === emailDomain,
  );
};

/**
 * Modal for viewing and editing role assignments for a selected account
 * @param {boolean} isOpen - Whether the modal is open
 * @param {() => void} onClose - Callback when modal closes
 * @param {AccountWithRoleAssignments} selectedAccount - Pre-selected account for edit mode; omit for add mode
 * @param {AccountWithRoleAssignments[]} accountsWithRoleAssignments - Currently assigned accounts, used to filter the add-mode dropdown
 * @param {Role[]} availableRoles - Available roles to assign
 * @param {Account[]} allAccounts - All accounts available for selection in add mode
 * @param {(account: AccountWithRoleAssignments, addRoleKeys: string[], removeRoleKeys: string[]) => Promise<void>} onSave - Callback when saving role changes
 * @returns {React.JSX.Element}
 */
export default function RoleAssignmentsModal({
  isOpen,
  onClose,
  allAccounts = [],
  accountsWithRoleAssignments = [],
  selectedAccount,
  availableRoles,
  isSaving = false,
  onSave,
}: RoleAssignmentsModalProps) {
  const isEditMode = !!selectedAccount;
  const allowedRoleAssignmentEmailDomains =
    useAppSelector((state) => state.config.allowedRoleAssignmentEmailDomains) ??
    [];

  const [selectedUser, setSelectedUser] =
    useState<AccountWithRoleAssignments | null>(selectedAccount || null);

  const originalRoleKeys = getRoleKeys(selectedUser);

  const [selectedRoleKeys, setSelectedRoleKeys] =
    useState<string[]>(originalRoleKeys);

  const addRoleKeys = selectedRoleKeys.filter(
    (roleKey) => !originalRoleKeys.includes(roleKey),
  );
  const removeRoleKeys = originalRoleKeys.filter(
    (roleKey) => !selectedRoleKeys.includes(roleKey),
  );

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleUserChange = (item: SearchableDropdownItem) => {
    const selectedOption = allAccounts.find(
      (account) => account.key === item.key,
    );

    if (selectedOption) {
      selectAccount({
        ...selectedOption,
        roleAssignments: [],
      });
    }
  };

  const handleRoleToggle = (roleKey: string) => {
    setSelectedRoleKeys((prevSelectedRoleKeys) => {
      if (prevSelectedRoleKeys.includes(roleKey)) {
        return prevSelectedRoleKeys.filter((key) => key !== roleKey);
      }

      return [...prevSelectedRoleKeys, roleKey];
    });
  };

  const validateDomainRequirements = () => {
    if (
      selectedUser &&
      hasPermittedRoleAssignmentEmailDomain(
        selectedUser.email,
        allowedRoleAssignmentEmailDomains,
      )
    ) {
      return [];
    }

    return availableRoles
      .filter(
        (role) =>
          selectedRoleKeys.includes(role.key) && role.requireAbbottDomain,
      )
      .map((role) => role.name);
  };

  const handleSave = () => {
    if (isSaving) {
      return;
    }

    const rolesRequiringAbbottDomain = validateDomainRequirements();

    if (rolesRequiringAbbottDomain.length > 0) {
      setErrorMessage(
        i18n.t('Modal_RoleAssignments.error_abbott_domain', {
          roles: rolesRequiringAbbottDomain.join(', '),
        }),
      );
      return;
    }

    setErrorMessage('');
    setShowConfirmation(true);
  };

  const selectAccount = (account: AccountWithRoleAssignments | null) => {
    setSelectedUser(account);
    setSelectedRoleKeys(getRoleKeys(account));
  };

  const dropdownOptions = useMemo(() => {
    return allAccounts
      .filter(
        (user) =>
          !accountsWithRoleAssignments.some(
            (account) => account.key === user.key,
          ),
      )
      .slice()
      .sort((a, b) =>
        getAccountIdentity(a).localeCompare(getAccountIdentity(b)),
      )
      .map((user) => ({
        key: user.key,
        label: getAccountIdentity(user),
        user,
      }));
  }, [allAccounts, accountsWithRoleAssignments]);

  const renderUserSelector = () => {
    if (isEditMode) {
      return (
        <Text type="$bodyBase.default">
          {getAccountIdentity(selectedAccount)}
        </Text>
      );
    }

    return (
      <SearchableDropdown
        items={dropdownOptions.map((option) => ({
          key: option.key,
          value: option.label,
        }))}
        selectedItem={
          selectedUser
            ? {
                key: selectedUser.key,
                value: getAccountIdentity(selectedUser),
              }
            : undefined
        }
        onChange={handleUserChange}
        disabled={isSaving}
      />
    );
  };

  const onConfirmSave = async () => {
    if (isSaving) {
      return;
    }

    if (!selectedUser) {
      setShowConfirmation(false);
      return;
    }

    try {
      await onSave(selectedUser, addRoleKeys, removeRoleKeys);
      setErrorMessage('');
      setShowConfirmation(false);
      onClose();
    } catch (err: any) {
      setShowConfirmation(false);
      console.error('Error saving role assignments:', err);
      const status = (err as ApiErrorResponse).status;

      if (status === 403) {
        setErrorMessage(i18n.t('Modal_RoleAssignments.error_forbidden'));
      } else if (status === 400 && err.message === 'roleRequiresAbbottDomain') {
        setErrorMessage(
          i18n.t('Modal_RoleAssignments.error_abbott_domain_generic'),
        );
      } else {
        setErrorMessage(i18n.t('Modal_RoleAssignments.error_generic'));
      }
    }
  };

  // sync state when modal opens
  useEffect(() => {
    if (isOpen) {
      selectAccount(selectedAccount || null);
      setShowConfirmation(false);
      setErrorMessage('');
    }
  }, [isOpen, selectedAccount]);

  return (
    <>
      <Dialog open={isOpen}>
        {/* Need to specify zIndex values so DropdownList popup renders above Dialog consistently */}
        <Dialog.Portal zIndex={1000}>
          <Dialog.Overlay key="overlay" zIndex={1000} />
          <Dialog.Content
            key="content"
            width="75%"
            maxHeight="90%"
            zIndex={1001}
            overflow="visible"
          >
            <VStack gap="$2">
              <Dialog.Title>
                <Text type="$h3.strong">
                  {i18n.t('Modal_RoleAssignments.title')}
                </Text>
              </Dialog.Title>
            </VStack>

            <VStack gap="$2" paddingVertical="$4" flex={1}>
              {errorMessage ? (
                <Highlight
                  variant="danger"
                  iconStart={
                    <IconBannersWarning
                      color="$neutral.0"
                      accentColor="$support.danger.default"
                    />
                  }
                  title={i18n.t('Modal_RoleAssignments.error_header')}
                  body={errorMessage}
                  testID="account-assignments-save-error-banner"
                />
              ) : null}

              <VStack gap="$2" paddingBottom="$4">
                <Text type="$bodyBase.strong">
                  {i18n.t('Modal_RoleAssignments.select_user_title')}
                </Text>
                {renderUserSelector()}
              </VStack>

              <VStack gap="$2" flex={1} minHeight={0}>
                <Text type="$bodyBase.strong">
                  {i18n.t('Modal_RoleAssignments.roles')}
                </Text>
                <ScrollView flex={1} minHeight={0}>
                  <CheckboxListCard separated>
                    {availableRoles.map((role) => {
                      return (
                        <CheckboxListItem
                          key={role.key}
                          title={role.name}
                          label={`${role.requireMfa ? i18n.t('Modal_RoleAssignments.mfaRequired') : ''}${role.requireMfa && role.requireAbbottDomain ? ', ' : ''}${role.requireAbbottDomain ? i18n.t('Modal_RoleAssignments.abbottDomainRequired') : ''}`}
                          checked={selectedRoleKeys.includes(role.key)}
                          onPress={() => handleRoleToggle(role.key)}
                          testID={`role-checkbox-${role.key}`}
                          aria-label={`${role.key} - ${role.description}`}
                          disabled={isSaving || !selectedUser}
                        />
                      );
                    })}
                  </CheckboxListCard>
                </ScrollView>
              </VStack>
            </VStack>

            <HStack gap="$2" justifyContent="flex-end">
              <Button
                variant="secondary"
                size="medium"
                label={i18n.t('Common.cancel')}
                onPress={onClose}
                disabled={isSaving}
                testID="account-assignments-cancel-button"
              />
              <Button
                variant="primary"
                size="medium"
                label={i18n.t('Common.save')}
                onPress={handleSave}
                disabled={
                  isSaving ||
                  !selectedUser ||
                  (addRoleKeys.length === 0 && removeRoleKeys.length === 0)
                }
                testID="account-assignments-save-button"
              />
            </HStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>

      {selectedUser ? (
        <RoleAssignmentsConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={onConfirmSave}
          selectedAccount={selectedUser}
          availableRoles={availableRoles}
          selectedRoleKeys={selectedRoleKeys}
          addRoleKeys={addRoleKeys}
          removeRoleKeys={removeRoleKeys}
          isSaving={isSaving}
        />
      ) : null}
    </>
  );
}
